/**
 * This is an app that combines a few other 'apps' together
 * so that they can access each other's data and actions.
 *
 * It does this by taking a definition from each app that
 * can describe certain store actions that it wants to receive
 * from other apps. Those actions are then monitored and passed
 * along to other apps that request them, as though they were
 * actions native to that 'app'.
 *
 * The benefit of this approeach is that most of each app
 * is ignorant that this is happening. The only change
 * each app needs to make is to register a definition object
 * instead of creating a store and rendering its own
 * component.
 *
 * The downside is the added complexity, but that is
 * restricted to this file.
 *
 * See one of the apps included below to see what a definition
 * should look like.
 *
 */

import React from 'react'
import {forEachObjIndexed, forEach, map, flatten, path, compose, curry, filter} from 'ramda'
import {render} from 'react-dom'
import {Provider} from 'react-redux'
import {combineReducers} from 'redux'
import reduceReducers from 'reduce-reducers'
import {hasProperties} from 'app/utils/objects'
import {filterKV, get} from 'app/utils/f'
import createStore from 'app/store/createStore'
import * as appKeys from 'app/school-master/app-keys'

const renderApp = (app) => {
    app.components && app.components.forEach(x => {
        render(
            <Provider store={app.store}>
                {x.component}
            </Provider>,
            x.el
        )
    })

    return app
}

const setStore = app => {
    app.store = createStore(app.reducer, app.state)
    return app
}

// adds a global reducer to each app that will monitor for actions
// and trigger than on the stores of other, interested apps.
const monitorActions = curry((apps, appKey, app) => {
    // find other apps that want actions from this app
    const getInterestedApps = compose(
        flatten,
        Object.values,
        map(filter(x => !!path(['pullActions', appKey], x))), // apps that want actions from this app
        filterKV((v, k) => k !== appKey) // exclude the current app
    )

    let interestedApps = getInterestedApps(apps)

    const godReducer = (state = {}, action = {}) => {
        // no interested apps
        if(!interestedApps.length) return state

        interestedApps.forEach(x => {
            const translatedActionType = path(['pullActions', appKey, action.type], x)

            // not monitoring this action
            if(!translatedActionType) return

            // redux is going to throw an exception if we encounter an infinite loop (e.g. when two apps are
            // listening to each other on the same action). so use it's mechanism for detecting that to save
            // us the trouble of implementing it.
            try {
                // trigger the action on the other app
                x.store && x.store.dispatch(Object.assign({}, action, {type: translatedActionType}))
            }
            catch(_) { }
        })

        return state
    }

    const monitoredReducer = reduceReducers(
        app.reducer,
        godReducer
    )

    app.reducer = monitoredReducer
    app.updateReducerOnNewApp = () => {
        interestedApps = getInterestedApps(apps)
    }

    return app
})

const runOnLoad = (app) => {
    if(app.onLoad) app.onLoad(app.store)

    return app
}

let apps = {}

window.AboundSchoolMaster = {
    // this function allows the apps to register themselves, so this
    // bundle doesn't get huge by aggresively including all apps that
    // might use this functionality. each apps[key] value is an array
    // of apps registered at that key, so can register more than one
    // app at a single key
    registerApp: (appKey, app) => {
        if(!apps[appKey]) apps[appKey] = []

        apps[appKey].push(compose(
            renderApp,
            runOnLoad,
            setStore,
            monitorActions(apps, appKey)
        )(app))

        forEachObjIndexed(forEach(x => {
            x.updateReducerOnNewApp()
        }), apps)

        return apps[appKey].length - 1 // return the index of the app, in this key
    },

    getStore: (appKey, i = 0) => {
        return path([appKey, i, 'store'], apps)
    },
}



// WEBPACK FOOTER //
// ./react/school-master.js